import React, { useState } from 'react';
import { QuoteData, TransportRoute } from '../../App';
import { TruckIcon, MapPinIcon, CalendarIcon, ChevronDownIcon, PlusCircleIcon, EditIcon, TrashIcon } from 'lucide-react';
interface TransportProps {
  quoteData: QuoteData;
  setQuoteData: (data: QuoteData) => void;
}
export function Transport({
  quoteData,
  setQuoteData
}: TransportProps) {
  const [routes, setRoutes] = useState<TransportRoute[]>(quoteData.transportRoutes.length > 0 ? quoteData.transportRoutes : [{
    id: '1',
    from: 'AISco - Albacete',
    to: 'AISco - Albacete',
    type: '',
    quantity: 0,
    unitPrice: 0
  }]);
  const handleDeliveryDateChange = (date: string) => {
    setQuoteData({
      ...quoteData,
      deliveryDate: date
    });
  };
  const handleIncotermChange = (incoterm: string) => {
    setQuoteData({
      ...quoteData,
      incoterm
    });
  };
  const handleTransportInsuranceChange = (checked: boolean) => {
    setQuoteData({
      ...quoteData,
      transportInsurance: checked
    });
  };
  const handleAddRoute = () => {
    const newRoute: TransportRoute = {
      id: Date.now().toString(),
      from: 'AISco - Albacete',
      to: 'AISco - Albacete',
      type: '',
      quantity: 0,
      unitPrice: 0
    };
    const updatedRoutes = [...routes, newRoute];
    setRoutes(updatedRoutes);
    setQuoteData({
      ...quoteData,
      transportRoutes: updatedRoutes
    });
  };
  const handleRouteChange = (id: string, field: keyof TransportRoute, value: string | number) => {
    const updatedRoutes = routes.map(route => route.id === id ? {
      ...route,
      [field]: value
    } : route);
    setRoutes(updatedRoutes);
    setQuoteData({
      ...quoteData,
      transportRoutes: updatedRoutes
    });
  };
  const handleDeleteRoute = (id: string) => {
    const updatedRoutes = routes.filter(route => route.id !== id);
    setRoutes(updatedRoutes);
    setQuoteData({
      ...quoteData,
      transportRoutes: updatedRoutes
    });
  };
  const calculateTotal = () => {
    return routes.reduce((sum, route) => sum + route.quantity * route.unitPrice, 0);
  };
  return <div className="w-full flex gap-6" data-id="element-1495">
      {/* Left Panel - Info */}
      <div className="w-64 flex-shrink-0" data-id="element-1496">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 sticky top-6" data-id="element-1497">
          <div className="flex items-center gap-3 mb-4" data-id="element-1498">
            <div className="w-10 h-10 bg-blue-600 rounded-lg flex items-center justify-center" data-id="element-1499">
              <TruckIcon className="w-5 h-5 text-white" data-id="element-1500" />
            </div>
            <h3 className="text-lg font-semibold text-blue-600" data-id="element-1501">Transport</h3>
          </div>
          <p className="text-sm text-gray-600 leading-relaxed" data-id="element-1502">
            Defines all transport-related terms required to process this quote.
          </p>
          <div className="mt-6 space-y-4" data-id="element-1503">
            <div className="flex items-start gap-3" data-id="element-1504">
              <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1505" />
              <div data-id="element-1506">
                <p className="text-xs font-semibold text-gray-700 mb-1" data-id="element-1507">From</p>
                <p className="text-sm text-gray-900" data-id="element-1508">AISco - Albacete</p>
              </div>
            </div>
            <div className="flex items-start gap-3" data-id="element-1509">
              <MapPinIcon className="w-5 h-5 text-gray-400 mt-0.5" data-id="element-1510" />
              <div data-id="element-1511">
                <p className="text-xs font-semibold text-gray-700 mb-1" data-id="element-1512">To</p>
                <p className="text-sm text-gray-900" data-id="element-1513">AISco - Albacete</p>
              </div>
            </div>
          </div>
        </div>
      </div>
      {/* Right Panel - Form */}
      <div className="flex-1 space-y-6" data-id="element-1514">
        {/* Delivery Info */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1515">
          <div className="grid grid-cols-2 gap-6" data-id="element-1516">
            <div data-id="element-1517">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1518">
                Desired delivery date
              </label>
              <div className="relative" data-id="element-1519">
                <input type="date" value={quoteData.deliveryDate} onChange={e => handleDeliveryDateChange(e.target.value)} placeholder="Select date" className="w-full px-4 py-3 bg-white border-2 border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-1520" />
              </div>
            </div>
            <div data-id="element-1521">
              <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-1522">
                Incoterm
              </label>
              <div className="relative" data-id="element-1523">
                <select value={quoteData.incoterm} onChange={e => handleIncotermChange(e.target.value)} className="w-full px-4 py-3 pr-10 bg-white border-2 border-gray-300 rounded-lg appearance-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all text-gray-900" data-id="element-1524">
                  <option value="EXW" data-id="element-1525">EXW - Ex Works</option>
                  <option value="FCA" data-id="element-1526">FCA - Free Carrier</option>
                  <option value="CPT" data-id="element-1527">CPT - Carriage Paid To</option>
                  <option value="CIP" data-id="element-1528">
                    CIP - Carriage and Insurance Paid To
                  </option>
                  <option value="DAP" data-id="element-1529">DAP - Delivered at Place</option>
                  <option value="DPU" data-id="element-1530">DPU - Delivered at Place Unloaded</option>
                  <option value="DDP" data-id="element-1531">DDP - Delivered Duty Paid</option>
                </select>
                <ChevronDownIcon className="absolute right-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" data-id="element-1532" />
              </div>
            </div>
          </div>
        </div>
        {/* Transport Routes Table */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden" data-id="element-1533">
          <div className="overflow-x-auto" data-id="element-1534">
            <table className="w-full" data-id="element-1535">
              <thead data-id="element-1536">
                <tr className="bg-gray-50 border-b border-gray-200" data-id="element-1537">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1538">
                    From
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1539">
                    To
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1540">
                    Type of Transportation
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1541">
                    Quantity
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1542">
                    Unit price
                  </th>
                  <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600 uppercase tracking-wider" data-id="element-1543">
                    Total price
                  </th>
                  <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider w-24" data-id="element-1544">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100" data-id="element-1545">
                {routes.map(route => <tr key={route.id} className="hover:bg-gray-50 transition-colors" data-id="element-1546">
                    <td className="px-4 py-4" data-id="element-1547">
                      <input type="text" value={route.from} onChange={e => handleRouteChange(route.id, 'from', e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1548" />
                    </td>
                    <td className="px-4 py-4" data-id="element-1549">
                      <input type="text" value={route.to} onChange={e => handleRouteChange(route.id, 'to', e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1550" />
                    </td>
                    <td className="px-4 py-4" data-id="element-1551">
                      <select value={route.type} onChange={e => handleRouteChange(route.id, 'type', e.target.value)} className="w-full px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm" data-id="element-1552">
                        <option value="" data-id="element-1553">Select type...</option>
                        <option value="truck" data-id="element-1554">Truck</option>
                        <option value="ship" data-id="element-1555">Ship</option>
                        <option value="air" data-id="element-1556">Air</option>
                        <option value="rail" data-id="element-1557">Rail</option>
                      </select>
                    </td>
                    <td className="px-4 py-4" data-id="element-1558">
                      <input type="number" min="0" value={route.quantity} onChange={e => handleRouteChange(route.id, 'quantity', Number(e.target.value))} className="w-20 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-center" data-id="element-1559" />
                    </td>
                    <td className="px-4 py-4" data-id="element-1560">
                      <input type="number" min="0" step="0.01" value={route.unitPrice} onChange={e => handleRouteChange(route.id, 'unitPrice', Number(e.target.value))} className="w-28 px-3 py-2 bg-white border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm text-right" data-id="element-1561" />
                    </td>
                    <td className="px-4 py-4 text-right" data-id="element-1562">
                      <span className="font-semibold text-gray-900" data-id="element-1563">
                        {(route.quantity * route.unitPrice).toFixed(2)}
                      </span>
                    </td>
                    <td className="px-4 py-4" data-id="element-1564">
                      <div className="flex items-center justify-center gap-1" data-id="element-1565">
                        <button onClick={() => handleDeleteRoute(route.id)} className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete" data-id="element-1566">
                          <TrashIcon className="w-4 h-4" data-id="element-1567" />
                        </button>
                      </div>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-200" data-id="element-1568">
            <button onClick={handleAddRoute} className="flex items-center gap-2 px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium text-sm" data-id="element-1569">
              <PlusCircleIcon className="w-4 h-4" data-id="element-1570" />
              Add Route
            </button>
          </div>
        </div>
        {/* Transport Insurance */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6" data-id="element-1571">
          <label className="flex items-center gap-3 cursor-pointer" data-id="element-1572">
            <input type="checkbox" checked={quoteData.transportInsurance} onChange={e => handleTransportInsuranceChange(e.target.checked)} className="w-5 h-5 text-blue-600 border-gray-300 rounded focus:ring-2 focus:ring-blue-500" data-id="element-1573" />
            <span className="text-sm font-medium text-gray-700" data-id="element-1574">
              Add transport insurance
            </span>
          </label>
        </div>
        {/* Total */}
        <div className="bg-gray-50 rounded-xl border border-gray-200 p-6" data-id="element-1575">
          <div className="flex items-center justify-between" data-id="element-1576">
            <span className="text-lg font-semibold text-gray-900" data-id="element-1577">
              Total transport
            </span>
            <span className="text-2xl font-bold text-gray-900" data-id="element-1578">
              {calculateTotal().toFixed(2)}
            </span>
          </div>
        </div>
      </div>
    </div>;
}